import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Pencil, PlusCircleIcon, FileText, Loader2 } from "lucide-react";
import { useEffect, useState } from "react";
import { SheetService } from "@/services/api/sheet";
import { SheetTemplate } from "@/utils/entities";

type AddLetterMode = "choose" | "create" | "load-template";

type Props = {
  isUpdate?: boolean;
  letter?: string;
  sheetId?: string;
  onSave: (name: string) => Promise<void>;
  onLetterCopied?: () => void | Promise<void>;
};

export default function ModalAddLetter({
  onSave,
  letter,
  isUpdate,
  sheetId,
  onLetterCopied,
}: Props) {
  const [isOpen, setIsOpen] = useState(false);
  const [name, setName] = useState("");
  const [error, setError] = useState<string>();
  const [mode, setMode] = useState<AddLetterMode>("choose");
  const [templates, setTemplates] = useState<SheetTemplate[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState<SheetTemplate | null>(
    null
  );
  const [selectedLetterIndex, setSelectedLetterIndex] = useState<number | null>(
    null
  );
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [loadingTemplateDetail, setLoadingTemplateDetail] = useState(false);
  const [copying, setCopying] = useState(false);

  useEffect(() => {
    if (letter) setName(letter);
  }, [letter]);

  const handleChangeInput = (value: string) => {
    if (error) {
      setError(undefined);
      return;
    }
    setName(value);
  };

  const onOpen = () => {
    setIsOpen(true);
    setMode(sheetId && !letter ? "choose" : "create");
    setSelectedTemplate(null);
    setSelectedLetterIndex(null);
    if (sheetId && !letter) {
      setLoadingTemplates(true);
      SheetService.getAllTemplates()
        .then(setTemplates)
        .finally(() => setLoadingTemplates(false));
    }
  };

  const onClose = () => {
    setName("");
    setError(undefined);
    setMode("choose");
    setSelectedTemplate(null);
    setSelectedLetterIndex(null);
    setIsOpen(false);
  };

  const handleSelectTemplate = async (templateId: string) => {
    setLoadingTemplateDetail(true);
    try {
      const template = await SheetService.getOneTemplate(templateId);
      setSelectedTemplate(template);
      setSelectedLetterIndex(null);
    } finally {
      setLoadingTemplateDetail(false);
    }
  };

  const handleCopyLetter = async () => {
    if (!sheetId || selectedTemplate?.id == null || selectedLetterIndex == null)
      return;
    setCopying(true);
    try {
      await SheetService.copyLetterFromTemplate(
        sheetId,
        selectedTemplate.id,
        selectedLetterIndex
      );
      onClose();
      await onLetterCopied?.();
    } finally {
      setCopying(false);
    }
  };

  const handleSave = async () => {
    if (!name) {
      setError("Adicione um nome para a 'Letra'");
      return;
    }
    onClose();
    await onSave(name);
  };

  const letters = Array.isArray(selectedTemplate?.data)
    ? selectedTemplate.data
    : [];
  const showLoadOption = sheetId && !letter;

  return (
    <Dialog open={isOpen}>
      {!letter ? (
        <Button onClick={onOpen}>
          <PlusCircleIcon className="mr-2 w-4 h-4" /> Adicionar "Letra"
        </Button>
      ) : (
        isUpdate && (
          <Button onClick={onOpen} className="ml-4">
            <Pencil className="mr-2 w-4 h-4" /> Editar "Letra"
          </Button>
        )
      )}

      <DialogContent className="sm:max-w-md bg-white">
        <DialogHeader>
          {!letter ? (
            <DialogTitle>
              {mode === "choose"
                ? "Como deseja adicionar a Letra?"
                : mode === "load-template"
                  ? "Carregar letra de um template"
                  : "Crie uma nova Letra"}
            </DialogTitle>
          ) : (
            <DialogTitle>Atualizar nome da "Letra"</DialogTitle>
          )}
          <DialogDescription className="text-xs">
            {mode === "create" || (mode === "choose" && !showLoadOption)
              ? "Exemplo: A, B, C ou Segunda á Sexta."
              : mode === "choose"
                ? "Crie do zero ou carregue de um template existente."
                : "Selecione um template e depois a letra para copiar."}
          </DialogDescription>
        </DialogHeader>

        {mode === "choose" && showLoadOption && (
          <div className="flex flex-col gap-3 mt-3">
            <Button
              type="button"
              variant="outline"
              className="justify-start"
              onClick={() => setMode("create")}
            >
              <PlusCircleIcon className="mr-2 w-4 h-4" /> Criar nova letra
            </Button>
            <Button
              type="button"
              variant="outline"
              className="justify-start"
              onClick={() => setMode("load-template")}
            >
              <FileText className="mr-2 w-4 h-4" /> Carregar de um template
            </Button>
          </div>
        )}

        {mode === "create" && (
          <div className="flex items-center space-x-2 mt-3">
            <div className="grid flex-1 gap-2">
              <Label htmlFor="link">Nome</Label>
              <Input
                id="link"
                value={name}
                className={error ? "border-red-600 border-[1px] border-solid" : ""}
                onChange={(e) => handleChangeInput(e.target.value)}
              />
              {error && (
                <div className="text-sm text-red-600 mt-0">{error}</div>
              )}
            </div>
          </div>
        )}

        {mode === "load-template" && (
          <div className="flex flex-col gap-4 mt-3">
            <div className="grid gap-2">
              <Label>Template</Label>
              {loadingTemplates ? (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Carregando templates...
                </div>
              ) : (
                <select
                  className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                  value={selectedTemplate?.id ?? ""}
                  onChange={(e) => {
                    const id = e.target.value;
                    if (id) handleSelectTemplate(id);
                    else setSelectedTemplate(null);
                  }}
                >
                  <option value="">Selecione um template</option>
                  {templates.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.name}
                    </option>
                  ))}
                </select>
              )}
            </div>

            {loadingTemplateDetail && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="w-4 h-4 animate-spin" />
                Carregando letras do template...
              </div>
            )}

            {selectedTemplate && !loadingTemplateDetail && (
              <div className="grid gap-2">
                <Label>Letras do template</Label>
                <div className="border rounded-md divide-y max-h-40 overflow-y-auto">
                  {letters.length === 0 ? (
                    <div className="p-3 text-sm text-muted-foreground">
                      Nenhuma letra neste template.
                    </div>
                  ) : (
                    letters.map((item, index) => (
                      <button
                        key={index}
                        type="button"
                        onClick={() =>
                          setSelectedLetterIndex(
                            selectedLetterIndex === index ? null : index
                          )
                        }
                        className={`w-full text-left px-3 py-2 text-sm hover:bg-muted/50 transition-colors ${
                          selectedLetterIndex === index
                            ? "bg-primary/10 text-primary font-medium"
                            : ""
                        }`}
                      >
                        {item.name}
                        {item.exercises?.length
                          ? ` (${item.exercises.length} exercício(s))`
                          : ""}
                      </button>
                    ))
                  )}
                </div>
              </div>
            )}

            {selectedTemplate && selectedLetterIndex != null && (
              <Button
                type="button"
                onClick={handleCopyLetter}
                disabled={copying}
                className="w-full"
              >
                {copying ? (
                  <Loader2 className="mr-2 w-4 h-4 animate-spin" />
                ) : null}
                Copiar letra
              </Button>
            )}
          </div>
        )}

        <DialogFooter className="sm:justify-end">
          {mode === "choose" && showLoadOption ? (
            <Button type="button" variant="secondary" onClick={onClose}>
              Cancelar
            </Button>
          ) : mode === "create" ? (
            <>
              <Button type="button" variant="secondary" onClick={onClose}>
                Cancelar
              </Button>
              <Button type="button" onClick={handleSave}>
                Salvar
              </Button>
            </>
          ) : mode === "load-template" ? (
            <>
              <Button
                type="button"
                variant="secondary"
                onClick={() => {
                  setSelectedTemplate(null);
                  setSelectedLetterIndex(null);
                  setMode("choose");
                }}
              >
                Voltar
              </Button>
              <Button type="button" variant="secondary" onClick={onClose}>
                Cancelar
              </Button>
            </>
          ) : (
            <Button type="button" variant="secondary" onClick={onClose}>
              Cancelar
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
